/**
 * The half of the telephone lease that crosses the machine boundary.
 *
 * `useDeviceLease` settles which window of ONE browser profile holds the phone,
 * over a BroadcastChannel. A channel stops at the machine: a laptop and a
 * desktop each win their own local vote, each registers the same Twilio
 * identity, Twilio delivers the call to one of them, and the other sits there
 * saying "This window rings" while never ringing. That is the 2026-08-11
 * production failure one step further out than the fix that shipped for it.
 *
 * So a window that has won locally beats to the server, which runs the same
 * vote across everything the operator has open (`lib/lease.ts` in the API). The
 * two layers are ordered rather than merged: losing the local vote means not
 * beating at all, because a background tab has no business bidding for a
 * telephone against another machine.
 *
 * ⚠ IT NEVER DROPS THE PHONE MID-CALL. `mine` going false tears down the Twilio
 * `Device`, which drops a live conversation. The server already refuses to move
 * a busy holder; this hold is the second net, for the case where the answer was
 * decided a moment before the call arrived.
 */
import { useEffect, useRef, useState } from "react";
import { API_BASE } from "../../../lib/api";
import { beatCallPresence, releaseCallPresence, type CallPresenceReach } from "../../../lib/calls";
import { windowClaimedAt, windowId } from "./windowActivity";

/**
 * How long to wait before the first answer arrives, and the fallback if none
 * ever does. The server sends its own number back with every answer.
 */
const DEFAULT_BEAT_MS = 15_000;

export interface CallPresenceOptions {
  /**
   * Beat at all. False for a window that is not a candidate: no permission,
   * inbound switched off, or the local vote already lost.
   */
  enabled: boolean;
  /**
   * How far this window has got with Twilio.
   *
   * Passed on rather than reduced to a boolean here: "standing by" and "Twilio
   * refused us" are opposites to the lease, and flattening them is what makes a
   * telephone that either deadlocks or flaps. See `lib/lease.ts` in the API.
   */
  reach: CallPresenceReach;
  /** A call is up here. Read through a function so a late answer sees it now. */
  busy: () => boolean;
}

/**
 * True while this window is the one the telephone should ring in.
 *
 * Starts true, like the local vote and for the same reason: the ordinary
 * installation is one operator at one machine, and a phone that waits for a
 * server round trip before it will ring is a phone that misses the first call
 * after every reload.
 */
export function useCallPresence(opts: CallPresenceOptions): boolean {
  const { enabled, reach, busy } = opts;
  const [mine, setMine] = useState(true);

  const busyRef = useRef(busy);
  const reachRef = useRef(reach);
  /** The current beat, so the effect below can trigger one out of turn. */
  const beatRef = useRef<() => void>(() => {});
  useEffect(() => {
    busyRef.current = busy;
  }, [busy]);

  // Reach is worth a beat of its own rather than waiting for the timer. A window
  // whose registration Twilio has just refused is holding a telephone it cannot
  // answer, and up to fifteen seconds of that is fifteen seconds in which no
  // other machine may take the call.
  useEffect(() => {
    if (reachRef.current === reach) return;
    reachRef.current = reach;
    beatRef.current();
  }, [reach]);

  useEffect(() => {
    if (!enabled) {
      // Not a candidate. Say so once - a window that stops beating would
      // otherwise keep the phone until the server's timeout ran out.
      setMine(true);
      return;
    }

    const id = windowId();
    let stopped = false;
    let timer = 0;

    const beat = async () => {
      if (stopped) return;
      try {
        const answer = await beatCallPresence({
          windowId: id,
          claimedAt: windowClaimedAt(),
          reach: reachRef.current,
          busy: busyRef.current(),
        });
        if (stopped) return;
        // The one hold. Losing the vote during a call is remembered by the next
        // beat rather than acted on now - see the ⚠ at the top.
        if (!answer.mine && busyRef.current()) return;
        setMine(answer.mine);
        schedule(answer.beatMs);
      } catch {
        // The API is unreachable, or this operator lost the permission. Either
        // way the honest answer is the one the browser can still defend on its
        // own: the local vote said this window holds it. Falling to false here
        // would take the phone off the only machine in the account over a
        // dropped request.
        if (!stopped) schedule(DEFAULT_BEAT_MS);
      }
    };

    function schedule(ms: number) {
      window.clearTimeout(timer);
      // A timeout chain rather than an interval: a beat that takes four seconds
      // must not be followed by the next one four seconds later and then two of
      // them a second apart. The gap is measured from the answer.
      timer = window.setTimeout(() => void beat(), Math.max(5_000, ms));
    }

    // Now, and again whenever the operator arrives in this window. The local
    // vote has just stamped a fresh claim, and this is what carries it across
    // the machine boundary - without it, moving to the other computer would
    // take up to a full beat to be felt.
    const onActive = () => {
      if (document.visibilityState !== "visible") return;
      void beat();
    };
    beatRef.current = () => void beat();
    void beat();
    document.addEventListener("visibilitychange", onActive);
    window.addEventListener("focus", onActive);

    const onHide = () => {
      // The browser's last chance to say anything. A holder that closes in
      // silence parks the telephone for the length of the server's timeout,
      // during which the operator's remaining window shows "ready" and does not
      // ring. `sendBeacon` because a normal request is cancelled with the page.
      const body = JSON.stringify({ windowId: id });
      const sent =
        typeof navigator.sendBeacon === "function" &&
        navigator.sendBeacon(
          `${API_BASE}/admin/crm/calls/presence/release`,
          new Blob([body], { type: "application/json" }),
        );
      if (!sent) void releaseCallPresence(id).catch(() => {});
    };
    window.addEventListener("pagehide", onHide);

    return () => {
      stopped = true;
      beatRef.current = () => {};
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onActive);
      window.removeEventListener("focus", onActive);
      window.removeEventListener("pagehide", onHide);
      void releaseCallPresence(id).catch(() => {});
    };
  }, [enabled]);

  return mine;
}
